import findKey from 'lodash.findkey';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, UrlTree, RouterEvent } from '@angular/router';

import { AUTH_LOGIN_LINK } from '~shared/constants/links.constants';
import { PAGE_ALIASES_MAP } from '../constants/page.constant';
import { PageData } from '../models';

@Injectable({
  providedIn: 'root',
})
export class PageService implements OnDestroy {

  public readonly currentPageAlias$: Observable<string | undefined>;
  private readonly pageAliasesMap = PAGE_ALIASES_MAP;
  private destroy$ = new Subject<void>();

  constructor(
    private readonly router: Router,
  ) {
    const currentPageAliasSubject$ = new ReplaySubject<string | undefined>(1);

    this.router.events.pipe(
      filter((event: RouterEvent) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => this.getPageAlias(event.urlAfterRedirects)),
      takeUntil(this.destroy$)
    ).subscribe(currentPageAliasSubject$);

    this.currentPageAlias$ = currentPageAliasSubject$.asObservable();
  }

  public getPageAlias(url: string): string | undefined {
    const pageData = this.getPageData(url);

    return pageData && pageData.alias;
  }

  public isPageAnonymous(url: string): boolean {
    const pageData = this.getPageData(url);

    return pageData && pageData.anonymous;

  }

  public navigateByPageAlias(targetPageAlias: string, routerOptions?): Promise<boolean> {
    const pageUrl = findKey(PAGE_ALIASES_MAP, pageAlias => pageAlias.alias === targetPageAlias);
    return this.router.navigate([pageUrl], routerOptions);
  }

  public getLoginUrlTree(url?: string): UrlTree {
    const urlTree = this.router.parseUrl(url || this.router.url);
    const { backUrl } = urlTree.queryParams;
    const loginUrlTree = this.router.parseUrl(AUTH_LOGIN_LINK);
    const currentPageAlias = this.getPageAlias(urlTree.toString());

    if (backUrl) {
      loginUrlTree.queryParams = { backUrl };
    } else if (!['login', 'logout'].includes(currentPageAlias)) {
        loginUrlTree.queryParams = {
          backUrl: urlTree.toString(),
        };
    }

    return loginUrlTree;
  }

  public currentRoute() {
    return this.router.url;
  }

  public pathStartsWith(path: string): boolean {
    return this.router.url.startsWith(path);
  }

  public pathEndsWith(path: string): boolean {
    return this.router.url.endsWith(path);
  }

  private getPageData(url: string): PageData | undefined {
    const currentPageAliases = Object.keys(this.pageAliasesMap).filter((pageAlias) => url.startsWith(pageAlias));
    // get last index; cover matching '/' path
    const currentPageAlias = currentPageAliases[currentPageAliases.length - 1];

    return this.pageAliasesMap[currentPageAlias];
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
